// --------------------- Import Global ---------------------
import { useState } from "react";
// --------------------- Import Uteis ---------------------
import { numberPadrao } from "../../../uteis/formatPhoneNumber";
// --------------------- Import Services ---------------------
import { cadastrarLead } from "../../../services/cadastrarLeadService";
import { buscarLeadsByCelService } from "../../../services/buscarLeadsByCelService";
import { buscarFonteByNameService } from "../../../services/buscarFonteByNameService";
import { buscarGrupoByNameService } from "../../../services/buscarGrupoByNameService";
import { cadastrarPostagemService } from "../../../services/cadastrarPostagemService";
import { buscarUsuarioProprietarioByCel } from "../../../services/buscarUsuarioProprietarioByCelService";
import { PostagemData } from "../Interface/PostagemData";

const FONTE_PADRAO = "site"; // ou "Site", confirme no banco
const GRUPO_PADRAO = "sem Grupo"; // confirme no banco

type CadastroResultado = {
  sucesso: boolean;
  pessoaId: number | null;
};

type LeadLookupResult = {
  leadId: number;
  pessoaId: number | null;
};

export const useCadastrarPostagem = () => {
  const [isLoadingCadastro, setIsLoadingCadastro] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const resetError = () => setError(null);
  const resetSuccess = () => setSuccess(null);

  const getPessoaId = (data: any) => data?.pessoaId ?? data?.idPessoa ?? null;

  // --------------------------
  // Criar Lead
  // --------------------------
  const obterOuCadastrarLead = async (
    data: PostagemData
  ): Promise<LeadLookupResult> => {
    const celularFormatado = numberPadrao(data.celular);

    // 1. Primeiro: procura Lead existente
    const leadExistente = await buscarLeadsByCelService(celularFormatado);

    if (leadExistente) {
      return {
        leadId: leadExistente.id,
        pessoaId: getPessoaId(leadExistente),
      };
    }

    // 2. Procura proprietario
    const usuarioProprietario = await buscarUsuarioProprietarioByCel(
      celularFormatado
    );

    // Cria um lead com o mesmo pessoaId do proprietario.
    if (usuarioProprietario) {
      const leadProprietario = await cadastrarLead({
        nome: `${usuarioProprietario.nome} ${
          usuarioProprietario.sobrenome || ""
        }`,
        celular: celularFormatado,
        active: 1,
        status: "DIVULGAR",
        tipo: "PROPRIETARIO",
        origem: FONTE_PADRAO,
      });

      if (!leadProprietario?.id) {
        throw new Error("Falha ao cadastrar lead proprietario.");
      }

      return {
        leadId: leadProprietario.id,
        pessoaId:
          getPessoaId(leadProprietario) ?? getPessoaId(usuarioProprietario),
      };
    }

    // 3. Caso nao exista nada: cria Lead normal
    const novoLead = await cadastrarLead({
      nome: data.nome,
      celular: celularFormatado,
      active: 1,
      status: "DIVULGAR",
      tipo: "LEAD",
      origem: FONTE_PADRAO,
    });

    if (!novoLead?.id) {
      throw new Error("Falha ao cadastrar lead.");
    }

    return {
      leadId: novoLead.id,
      pessoaId: getPessoaId(novoLead),
    };
  };

  // --------------------------
  // Fonte
  // --------------------------
  const obterFontePadrao = async () => {
    try {
      const fonte = await buscarFonteByNameService(FONTE_PADRAO);

      if (!fonte?.id) {
        throw new Error(`Fonte "${FONTE_PADRAO}" nao encontrada.`);
      }

      return fonte.id;
    } catch (err) {
      console.error("Erro ao obter fonte padrao:", err);
      throw new Error('Erro ao carregar fonte padrao "SITE".');
    }
  };

  // --------------------------
  // Grupo
  // --------------------------
  const obterGrupoPadrao = async () => {
    try {
      const grupo = await buscarGrupoByNameService(GRUPO_PADRAO);
      if (!grupo?.id) {
        throw new Error(`Grupo "${GRUPO_PADRAO}" nao encontrado.`);
      }

      return grupo.id;
    } catch (err) {
      console.error("Erro ao obter grupo padrao:", err);
      throw new Error('Erro ao carregar grupo padrao "Sem Grupo".');
    }
  };

  // -----------------------------------------------------
  // RETRY INTELIGENTE PARA POSTAGEM
  // -----------------------------------------------------
  const tentarCadastrarPostagem = async (postagemData: any) => {
    const tentativasMax = 10;

    for (let i = 0; i < tentativasMax; i++) {
      try {
        console.log(`Tentativa ${i + 1} de cadastrar postagem...`);

        const postagem = await cadastrarPostagemService(postagemData);
        if (postagem) return postagem;
      } catch (err) {
        console.warn(`Tentativa ${i + 1} falhou. Nova tentativa...`);
      }

      // delay progressivo: 1s -> 2s -> 3s
      await new Promise((res) => setTimeout(res, 1000 * (i + 1)));
    }

    throw new Error("Falha ao cadastrar postagem apos varias tentativas.");
  };

  const cadastrar = async (
    data: PostagemData,
    objetivos: string[]
  ): Promise<CadastroResultado> => {
    setIsLoadingCadastro(true);
    setError(null);
    setSuccess(null);

    const inicio = Date.now();

    const precisaAnimacaoBusca =
      objetivos.includes("alugar") || objetivos.includes("trocar");

    const tempoMinimoAnimacao = precisaAnimacaoBusca ? 15000 : 0;

    try {
      // 1. Fonte e grupo fixos
      const idFonte = await obterFontePadrao();
      const idGrupo = await obterGrupoPadrao();
      const leadData = await obterOuCadastrarLead(data);

      const postagemData = {
        idLeads: leadData.leadId,
        idFonte,
        idGrupo,
        idEmpreendimento: data.idEmpreendimento,
        active: 1,
        dataDivulgacao: data.dataDivulgacao,
        tipoInteresse: data.tipoInteresse,
        troca: data.troca,
        aluguel: data.aluguel,
        compra: data.compra,
        venda: data.venda,
        dataIni: data.dataIni || null,
        dataFim: data.dataFim || null,
        observacoes: data.observacoes || "",
      };

      // Esse delay existe para reduzir problemas de concorrencia no backend
      // entre a criacao do lead e da postagem.
      const postagemCriada = await tentarCadastrarPostagem(postagemData);
      if (postagemCriada) {
        setSuccess("Informacao cadastrada com sucesso.");
        return {
          sucesso: true,
          pessoaId: leadData.pessoaId,
        };
      }

      setError("Erro ao cadastrar a postagem.");
      return {
        sucesso: false,
        pessoaId: null,
      };
    } catch (error) {
      console.error(error);
      setError("Erro inesperado ao cadastrar a postagem.");
      return {
        sucesso: false,
        pessoaId: null,
      };
    } finally {
      const duracao = Date.now() - inicio;
      if (duracao < tempoMinimoAnimacao) {
        await new Promise((res) =>
          setTimeout(res, tempoMinimoAnimacao - duracao)
        );
      }
      // setIsLoadingCadastro(false);
    }
  };

  return {
    cadastrar,
    isLoadingCadastro,
    success,
    error,
    resetError,
    resetSuccess,
  };
};
